/**
 * @fileoverview Shared sponsored-listing wire contracts (WEB-36).
 *
 * Mirrors the Prisma `SponsoredTier` enum (`packages/db-core/prisma/schema.prisma`)
 * and the `SponsoredMeta` shape attached by the shared server-side injector
 * (`services/backend-api/src/services/sponsoredSlotInjector.ts`, API-1361) to
 * any event it promotes into a sponsored slot on `/events/discover` (covers
 * both the plain `/discover` list and the category-filtered view — same
 * route, `?category=`), `/events/discover?category=`, and
 * `/recommendations/happening-now`.
 *
 * Frontend packages cannot import `db-core` directly (it is backend-only and
 * Prisma-coupled), so this is an independent, hand-kept mirror of the wire
 * shape. Keep `SponsoredWireTier` in sync with the Prisma `SponsoredTier`
 * enum (`STARTER | GROWTH | FEATURED_PLUS`) — a value here that the enum
 * doesn't have (or vice versa) is a hard drift bug.
 *
 * Distinct from `@loci/ui`'s `SponsoredBadge` `SponsoredTier` type, which is
 * lowercase (`'starter' | 'growth' | 'featured'`) and drives the atom's visual
 * treatment. Consumers map between the two explicitly (never assume a bare
 * `.toLowerCase()` — `FEATURED_PLUS` does not lowercase to a valid atom tier).
 *
 * @since WEB-36 (Featured / Sponsored Listings — M1 Revenue v0)
 */

/**
 * The always-visible paid-placement disclosure every client prints next to a
 * sponsored row (API-1857). One constant, read by the server injector
 * (`sponsoredSlotInjector.ts`) and the hero-slot resolver
 * (`sponsoredHeroSlot.ts`) so every client — web, MCP, a future mobile app —
 * receives the same honest words on the wire instead of only the web atoms
 * knowing them.
 *
 * Parity: `@loci/ui`'s `SponsoredBadge` keeps its OWN fixed, non-overridable
 * literal (FTC 16 CFR Part 255 rationale in that file); a parity test
 * (`SponsoredBadge.test.tsx`) fails if the two ever differ. This value is not
 * a way to reword the web pill, and there is no per-advertiser or per-tier
 * variant. Approved copy: DES-53.
 */
export const SPONSORED_DISCLOSURE_LABEL = 'Sponsored' as const;

/**
 * Relative path of the public sponsored-content disclosure policy page
 * (`apps/web/app/(apex)/legal/sponsored-content`). Clients resolve it
 * against the apex origin.
 */
export const SPONSORED_DISCLOSURE_URL = '/legal/sponsored-content' as const;

/** Wire-format sponsorship tier — mirrors the Prisma `SponsoredTier` enum. */
export type SponsoredWireTier = 'STARTER' | 'GROWTH' | 'FEATURED_PLUS';

/**
 * Sponsored-listing annotation attached to an event by `injectSponsoredSlots`.
 * Present ONLY on events promoted into a sponsored slot; organic events never
 * carry this field (honest-absence — omitted, never `null`).
 */
export interface SponsoredEventMeta {
  /** `SponsoredListing.id` — the paid placement record backing this slot. */
  listingId: string;
  /** `SponsoredListing.advertiserId` — the paying advertiser. */
  advertiserId: string;
  /** Sponsorship tier. Drives which visual treatment a card composes. */
  tier: SponsoredWireTier;
  /**
   * `Advertiser.name` — the paying advertiser's display name. Drives the
   * Featured+ "Presented by {advertiserName}" treatment (DES-50). Always
   * present (an advertiser row always has a name).
   */
  advertiserName: string;
  /**
   * `Advertiser.logoUrl` — the advertiser's brand logo, or `null` when they
   * have not supplied one. `null` is HONEST ABSENCE (API-1612): downstream
   * (Growth 24px card-footer logo, Featured+ "Presented by" brand logo) MUST
   * render nothing rather than a placeholder or broken image. Never an empty
   * string.
   */
  logoUrl: string | null;
  /**
   * The always-visible disclosure text ({@link SPONSORED_DISCLOSURE_LABEL},
   * `"Sponsored"`) — any client prints this verbatim next to the row
   * (API-1857). Always present; never per-advertiser or per-tier.
   */
  label: string;
  /**
   * Relative path of the disclosure policy page
   * ({@link SPONSORED_DISCLOSURE_URL}, `/legal/sponsored-content`).
   */
  disclosureUrl: string;
}

/**
 * The single eligible Featured+ sponsored listing surfaced to a landing
 * hero's "Presented by {advertiserName}" slot (WEB-538 / DES-50).
 *
 * Distinct from {@link SponsoredEventMeta} (which the `injectSponsoredSlots`
 * feed/map injector attaches to promoted event rows): the hero slot does NOT
 * flow through the discover injector. It is resolved independently by
 * `resolveSponsoredHeroSlot` (backend) from the SAME shared eligibility fetch
 * (`sponsoredEligibility.ts`) — one advertiser at a time, the highest-ranked
 * `ACTIVE` `FEATURED_PLUS` listing, or `null` when none qualifies (honest
 * absence — each hero then renders exactly as today, no empty scaffold).
 *
 * Featured+ ONLY: Growth/Starter listings never populate this slot, so `tier`
 * is narrowed to the single literal the hero treatment triggers on.
 */
export interface SponsoredHeroSlot {
  /** `SponsoredListing.id` — the paid placement backing the hero slot. */
  listingId: string;
  /** `SponsoredListing.advertiserId` — the paying advertiser. */
  advertiserId: string;
  /**
   * Always `'FEATURED_PLUS'` — the hero slot is a Featured+-exclusive
   * placement surface (pricing table "Placement surfaces" row). Narrowed to
   * the single literal so a consumer cannot render the treatment for a
   * non-Featured+ tier.
   */
  tier: 'FEATURED_PLUS';
  /**
   * `Advertiser.name` — drives the "Presented by {advertiserName}" text.
   * Always a non-empty string on the wire: `resolveSponsoredHeroSlot` returns
   * `null` rather than a slot with a blank advertiser name.
   */
  advertiserName: string;
  /**
   * `Advertiser.logoUrl` — the brand logo, or `null` (HONEST ABSENCE): the
   * hero renders "Presented by {advertiserName}" with no image, never a
   * broken/placeholder logo.
   */
  logoUrl: string | null;
  /**
   * The always-visible disclosure text ({@link SPONSORED_DISCLOSURE_LABEL})
   * — same value as {@link SponsoredEventMeta.label} (API-1857).
   */
  label: string;
  /** Relative path of the disclosure policy page ({@link SPONSORED_DISCLOSURE_URL}). */
  disclosureUrl: string;
  /**
   * The sponsored event backing the placement — the hero's "Presented by"
   * logo + name link to this event's detail page (`/event/<slug>`). The
   * highest-ranked eligible upcoming event for the winning listing.
   */
  event: {
    /** `Event.slug` — builds the `/event/<slug>` link target. */
    slug: string;
    /** `Event.title` — accessible link label context. */
    title: string;
  };
}
